import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { servicesApi } from '@/services/api/deprecated';
import { isEmptyString } from '@/utils/string';
import type { Workspace, IdentitiesResponse } from '../types';

interface UseVerifiedDomainsResult {
  domains: string[];
  isLoading: boolean;
  error: unknown;
}

export function useVerifiedDomains(workspaces: Workspace[] | null): UseVerifiedDomainsResult {
  const workspaceIds = useMemo(
    () =>
      workspaces
        ?.map((w) => w.id)
        .sort()
        .join(',') ?? null,
    [workspaces],
  );

  const {
    data: domains = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['verifiedDomains', workspaceIds],
    queryFn: async () => {
      if (workspaces == null || workspaces.length === 0) {
        return [];
      }

      const domainSet = new Set<string>();

      const workspacePromises = workspaces.map(async (workspace) => {
        try {
          const workspaceId = Number.parseInt(workspace.id, 10);
          if (Number.isNaN(workspaceId) || workspaceId <= 0) {
            return;
          }

          const { data } = await servicesApi.get<IdentitiesResponse>(
            `/environments/${workspaceId}/identities?type=email`,
          );

          if (data?.identities != null) {
            for (const identity of data.identities) {
              if (identity.verified === 'verified' && !isEmptyString(identity.email)) {
                const emailLower = identity.email.toLowerCase().trim();
                const atIndex = emailLower.indexOf('@');
                if (atIndex > 0 && atIndex < emailLower.length - 1) {
                  const domain = emailLower.substring(atIndex + 1);
                  if (!isEmptyString(domain)) {
                    domainSet.add(domain);
                  }
                }
              }
            }
          }
        } catch {
          // Ignore individual workspace failures
        }
      });

      await Promise.all(workspacePromises);
      return Array.from(domainSet).sort();
    },
    enabled: workspaces != null && workspaces.length > 0,
  });

  return { domains, isLoading, error };
}
